import { useEffect, useState } from "react";
import api from "../../lib/api";
import {
  Loader2, Building2, Users, CheckCircle2, Award, Send, ShieldCheck, Coins, Briefcase, ChevronRight,
} from "lucide-react";

const Card = ({ children, className = "", ...p }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`} {...p}>{children}</div>
);

const KPI = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className={`h-10 w-10 rounded-xl grid place-items-center ${accent}`}><Icon className="h-5 w-5" /></span>
    </div>
    <div className="num font-extrabold text-3xl text-ink mt-3">{value}</div>
    <div className="text-xs font-semibold text-ink-soft mt-0.5">{label}</div>
  </Card>
);

export default function AgencyDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/agency/dashboard").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="min-h-[40vh] grid place-items-center text-center"><div><p className="text-ink-soft text-sm">Couldn't load the agency dashboard.</p><button onClick={() => window.location.reload()} className="mt-3 text-sm font-bold text-white bg-brand px-5 py-2.5 rounded-full">Retry</button></div></div>;
  if (!d) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const k = d.kpis;
  const maxStage = Math.max(1, ...d.pipeline.map((s) => s.count));

  return (
    <div className="space-y-8" data-testid="agency-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand mb-1">
            <Briefcase className="h-3.5 w-3.5" /> Recruitment Agency
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{d.agency.name}</h1>
          <p className="text-ink-soft text-sm mt-1">{d.agency.subtitle}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-right">
          <div className="text-xs text-ink-faint font-semibold">Credits Remaining</div>
          <div className="num font-extrabold text-2xl text-brand">{k.credits_remaining}</div>
          <div className="text-[11px] text-ink-faint num">{k.credits_used} / {k.credits_purchased} used</div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-clients" icon={Building2} label="Clients" value={k.clients} accent="bg-brand-light text-brand" />
        <KPI testid="kpi-candidates" icon={Users} label="Active Candidates" value={k.active_candidates} accent="bg-indigo-100 text-indigo-600" />
        <KPI testid="kpi-interviews" icon={CheckCircle2} label="Interviews Completed" value={k.interviews_completed} accent="bg-emerald-100 text-emerald-600" />
        <KPI testid="kpi-ready" icon={ShieldCheck} label="Candidates Interview Ready" value={k.interview_ready} accent="bg-amber-100 text-amber-600" />
        <KPI testid="kpi-recommended" icon={Send} label="Candidates Recommended" value={k.recommended} accent="bg-violet-100 text-violet-600" />
        <KPI testid="kpi-published" icon={Award} label="Einstein Scores™ Published" value={k.published} accent="bg-sky-100 text-sky-600" />
        <KPI testid="kpi-credits" icon={Coins} label="Credits Remaining" value={k.credits_remaining} accent="bg-rose-100 text-rose-600" />
      </div>

      {/* Recruitment pipeline */}
      <Card className="p-6" data-testid="recruitment-pipeline">
        <div className="font-heading font-bold text-ink mb-1">Recruitment Pipeline</div>
        <p className="text-xs text-ink-soft mb-5">Candidates at each stage of the talent journey</p>
        <div className="space-y-2.5">
          {d.pipeline.map((s, i) => (
            <div key={s.stage} className="flex items-center gap-3" data-testid={`stage-${i}`}>
              <div className="w-48 shrink-0 text-sm font-semibold text-ink-soft flex items-center gap-2">
                <span className="num text-xs text-ink-faint w-4">{i + 1}</span> {s.stage}
              </div>
              <div className="flex-1 h-7 rounded-lg bg-slate-100 overflow-hidden">
                <div className="h-full rounded-lg bg-brand transition-all flex items-center justify-end px-2" style={{ width: `${Math.max(6, (s.count / maxStage) * 100)}%` }}>
                  <span className="num text-xs font-bold text-white">{s.count}</span>
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
